import Iconify from '@/components/iconify';
import useResponsive from '@/hooks/useResponsive';
import { Box, Button, Chip, Dialog, Drawer, IconButton, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { announcementShape } from './announcementShape';

const TITLE_ID = 'announcement-title';

const badgeSx = {
  alignSelf: 'flex-start',
  height: 22,
  fontSize: '11px',
  fontWeight: 700,
  bgcolor: 'primary.main',
  color: 'common.white',
  '& .MuiChip-label': { px: 0.75 },
};

SlideIndicator.propTypes = {
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
};

function SlideIndicator({ index, total }) {
  return (
    <Box
      role="img"
      aria-label={`Aviso ${index + 1} de ${total}`}
      sx={{ display: 'flex', justifyContent: 'center', gap: 0.75 }}
    >
      {Array.from({ length: total }, (_, position) => (
        <Box
          key={position}
          sx={{
            width: position === index ? 20 : 8,
            height: 8,
            borderRadius: 4,
            bgcolor: position === index ? 'primary.main' : 'divider',
            transition: (theme) => theme.transitions.create('width'),
          }}
        />
      ))}
    </Box>
  );
}

AnnouncementSlide.propTypes = {
  announcement: announcementShape.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  isMobile: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

function AnnouncementSlide({ announcement, index, total, isMobile, onBack, onNext, onClose }) {
  const { badge, title, description, image, content, learnMore, seeNow } = announcement;
  const isLast = index === total - 1;
  const titleRef = useRef(null);
  const previousIndexRef = useRef(index);

  // O Voltar some no 1º aviso: sem mover o foco, ele cai no body e o FocusTrap o devolve ao fundo do
  // modal, e quem usa teclado recomeça o Tab do início. No título, o leitor de tela também anuncia o
  // aviso novo. Na abertura o foco fica com o Dialog.
  useEffect(() => {
    if (previousIndexRef.current === index) return;
    previousIndexRef.current = index;
    titleRef.current?.focus();
  }, [index]);

  // O tema põe `capitalize` em todo botão, o que viraria "Saber Mais". O MUI dá `minWidth: 64` ao
  // botão: com o `flex: 1` do celular, a linha mede o botão por esses 64 px e o rótulo quebra ou
  // vaza, em vez de o botão descer de linha. `fit-content`, e não `max-content`: este alarga o botão
  // com rótulo longo além do modal em tela estreita, e um `maxWidth` não segura, porque o
  // `min-width` vence o `max-width`.
  const buttonSx = {
    textTransform: 'none',
    minWidth: 'fit-content',
    ...(isMobile && { flex: 1, minHeight: 48 }),
  };

  // O modal fica no DashboardLayout, que não desmonta ao trocar de rota: sem o onClose, ele
  // continuaria aberto por cima da tela de destino. Com Ctrl/Cmd/Shift o navegador abre a novidade
  // em outra aba e esta não sai do lugar, então o modal fica, e os avisos não viram vistos.
  const handleSeeNowClick = (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;
    onClose();
  };

  return (
    <Stack spacing={isMobile ? 2.5 : 3} sx={{ position: 'relative' }}>
      <IconButton
        aria-label="Fechar"
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: isMobile ? -24 : -16,
          right: isMobile ? -8 : -16,
          width: isMobile ? 44 : 40,
          height: isMobile ? 44 : 40,
          border: 1,
          borderColor: 'divider',
          color: 'text.secondary',
        }}
      >
        <Iconify icon="eva:close-fill" width={20} />
      </IconButton>

      <Stack spacing={1.25} sx={{ pr: 5 }}>
        {badge && <Chip label={badge} size="small" sx={badgeSx} />}
        <Typography
          ref={titleRef}
          id={TITLE_ID}
          tabIndex={-1}
          variant="h4"
          component="h2"
          color="text.primary"
          sx={{ outline: 'none' }}
        >
          {title}
        </Typography>
        {description && (
          <Typography variant={isMobile ? 'body2' : 'body1'} component="div" color="text.secondary">
            {description}
          </Typography>
        )}
      </Stack>

      {image && (
        <Box
          component="img"
          src={image.src}
          alt={image.alt}
          sx={{
            display: 'block',
            width: 1,
            aspectRatio: '16 / 9',
            objectFit: 'cover',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        />
      )}

      {content}

      {total > 1 && <SlideIndicator index={index} total={total} />}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {index > 0 && (
          <Button variant="text" color="inherit" onClick={onBack} sx={{ ...buttonSx, mr: 'auto' }}>
            Voltar
          </Button>
        )}
        {learnMore && !seeNow && (
          <Button
            variant="outlined"
            color="primary"
            href={learnMore.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={buttonSx}
          >
            {learnMore.label}
          </Button>
        )}
        {seeNow && (
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to={seeNow}
            onClick={handleSeeNowClick}
            sx={buttonSx}
          >
            Ver agora
          </Button>
        )}
        <Button
          variant={seeNow ? 'outlined' : 'contained'}
          color="primary"
          onClick={isLast ? onClose : onNext}
          sx={buttonSx}
        >
          {isLast ? 'Entendi' : 'Próximo'}
        </Button>
      </Box>
    </Stack>
  );
}

AnnouncementModal.propTypes = {
  announcements: PropTypes.arrayOf(announcementShape).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default function AnnouncementModal({ announcements, onClose }) {
  const isMobile = useResponsive('down', 'sm');
  const [index, setIndex] = useState(0);

  // Baixa as imagens dos próximos avisos já na abertura: sem isso, o Próximo mostra o quadro vazio
  // até o print chegar.
  useEffect(() => {
    announcements.forEach(({ image }) => {
      if (image) new Image().src = image.src;
    });
  }, [announcements]);

  const hasImage = announcements.some(({ image }) => image);
  const total = announcements.length;
  if (total === 0) return null;

  const current = Math.min(index, total - 1);

  const slide = (
    <AnnouncementSlide
      announcement={announcements[current]}
      index={current}
      total={total}
      isMobile={isMobile}
      onBack={() => setIndex(Math.max(current - 1, 0))}
      onNext={() => setIndex(Math.min(current + 1, total - 1))}
      onClose={onClose}
    />
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open
        onClose={onClose}
        PaperProps={{
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': TITLE_ID,
          sx: {
            borderTop: 1,
            borderColor: 'divider',
            borderRadius: '24px 24px 0 0',
            px: 3,
            pt: 1.5,
            pb: 3.5,
          },
        }}
      >
        <Box
          aria-hidden
          sx={{ width: 40, height: 4, borderRadius: 100, bgcolor: 'divider', mx: 'auto', mb: 3 }}
        />
        {slide}
      </Drawer>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      PaperProps={{
        sx: {
          // Com print, o modal alarga para os números da tela caberem legíveis.
          width: hasImage ? 640 : 520,
          maxWidth: 'calc(100% - 32px)',
          m: 2,
          p: 4,
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
        },
      }}
    >
      {slide}
    </Dialog>
  );
}
